import { memo, useEffect, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import { cn } from "~/utils/tailwind";

export interface ITextBoxProps {
  id?: string;
  name: string;
  dataBlockId?: string;
  label?: string;
  type?: string;
  disabled?: boolean;
  hint?: string;
  className?: string;
  maxLength?: any;
  isRequired?: boolean;
  showLabel?: boolean;
  hidden?: boolean;
  autoComplete?: any;
  onFocus?: any;
  onFocusOut?: (value: string) => void;
  showErrorMessage?: boolean;
  showAutocomplete?: boolean;
  autocompleteOptions?: any;
  onOptionSelect?: any;
  title?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; // Added onChange prop
}

const TextAreaField = ({
  id,
  name,
  dataBlockId,
  label,
  disabled,
  hint,
  className,
  isRequired = false,
  maxLength,
  showLabel = true,
  hidden,
  autoComplete,
  onFocus,
  showErrorMessage = true,
  showAutocomplete = false,
  autocompleteOptions = [],
  onOptionSelect,
  title = "",
  onChange,
  ...props
}: ITextBoxProps) => {
  const [focused, setFocused] = useState<boolean>(false);
  const { control } = useFormContext();
  const {
    field: { value, ref, onChange: onFieldChange, ...inputProps },
    fieldState,
  } = useController({ control, name, defaultValue: "" });

  const handleFocus = (e: any) => {
    //const value = e.target.value;
    //e.target.value = ""; // clear temporarily
    //e.target.value = value; // reset to same text — cursor moves to end

    setFocused(true);

    // 🟩 Select entire text when focus comes via Tab (keyboard navigation)
    requestAnimationFrame(() => {
      if (e.target && document.activeElement === e.target) {
        e.target.select();
      }
    });

    if (onFocus != null) {
      onFocus(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onFieldChange(e.target.value);

    if (onChange) {
      onChange(e);
    }
  };

  // const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
  //   const val = e.target.value;

  //   if (props.onFocusOut) {
  //     props.onFocusOut(val);
  //   }
  // };

  useEffect(() => {
    return () => {
      setFocused(false);
    };
  }, []);

  return (
    <div className={cn(showAutocomplete ? "" : "relative")} hidden={hidden}>
      {showLabel && (
        <label
          htmlFor={name}
          className="absolute z-1 text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/3 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 start-1 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto whitespace-nowrap"
        >
          <span className={cn(fieldState.error && "text-red-700")}>
            {label} {isRequired && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}
      <div
        className={cn(
          "rounded-md  pt-[13px] pb-[2px] px-0.5 shadow-sm placeholder:text-gray-400 sm:text-sm",
          fieldState.error
            ? "ring-2 ring-red-700 focus-within:ring-2 focus-within:ring-blue-500"
            : "ring-1 ring-insert ring-gray-300 focus-within:ring-2 focus-within:ring-blue-500"
        )}
      >
        <textarea
          id={id ?? name}
          aria-label={dataBlockId}
          title={title}
          className={cn(
            "block w-full h-8 pt-0 ps-2 uppercase rounded-md border-0 text-gray-900 focus:outline-none focus:ring-0 text-base sm:leading-4",
            { "bg-gray-100 cursor-not-allowed": disabled },
            className
          )}
          maxLength={maxLength}
          disabled={disabled}
          autoComplete={autoComplete}
          onFocus={handleFocus}
          value={value === null ? "" : value}
          ref={ref}
          {...inputProps}
          // onBlur={handleBlur}
          {...props}
          onChange={handleChange}
        />
      </div>
      {fieldState?.error?.message && showErrorMessage && (
        <span className="text-red-700 px-1 text-xs">
          {fieldState?.error?.message}
        </span>
      )}
      {hint && <span className="px-2 text-xs">{hint}</span>}
      {focused && showAutocomplete && value !== null && value.length > 0 && (
        <ul className="bg-neutral-100 absolute w-40 z-50">
          {autocompleteOptions
            .filter((opt: any) =>
              opt.label.toLowerCase().startsWith(value.toLowerCase())
            )
            .map((opt: any, index: number) => {
              return (
                <li
                  key={index}
                  className="hover:bg-red-300"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOptionSelect({ name, option: opt });
                    setFocused(false);
                  }}
                >
                  {opt.label}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

export default memo(TextAreaField);
